import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);

/**
 * Provides authentication state and helpers to the component tree.
 *
 * Persists the Google credential (ID token) in sessionStorage so the user
 * stays logged in across page refreshes within the same tab.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // Restore session on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem("admin_token");
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp > now) {
          setUser({
            name: decoded.name || decoded.given_name || "Admin",
            email: decoded.email,
            picture: decoded.picture,
          });
          setToken(storedToken);
        } else {
          sessionStorage.removeItem("admin_token");
        }
      } catch {
        sessionStorage.removeItem("admin_token");
      }
    }
  }, []);

  const login = useCallback((credentialResponse) => {
    const credential = credentialResponse.credential;
    try {
      const decoded = jwtDecode(credential);
      setUser({
        name: decoded.name || decoded.given_name || "Admin",
        email: decoded.email,
        picture: decoded.picture,
      });
      setToken(credential);
      sessionStorage.setItem("admin_token", credential);
    } catch (err) {
      console.error("Failed to decode Google credential");
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem("admin_token");
  }, []);

  /**
   * Check whether the current token is still valid.
   * Returns false (and logs the user out) if expired.
   */
  const isTokenValid = useCallback(() => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      if (decoded.exp && decoded.exp > now) {
        return true;
      }
    } catch {
      /* invalid token */
    }
    logout();
    return false;
  }, [token, logout]);

  const value = { user, token, login, logout, isTokenValid };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to consume the AuthContext.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
